import { useCallback, useEffect, useState, useMemo } from 'react';
import { Filter, FilterValue } from '@/shared/types/filters';
export interface UseFiltersProps {
  filters: Filter[];
  onClear?: () => void;
  onApply?: (filterStates: Record<string, FilterValue>) => void;
}

export interface UseFiltersReturn {
  filterStates: Record<string, FilterValue>;
  handleFilterChange: (filterLabel: string, newValue: FilterValue, filter: Filter) => void;
  handleClear: () => void;
  handleApply: () => void;
}

// Constantes para tipos de filtros
export const FILTER_TYPES = {
  DROPDOWN: 'dropdown',
  MULTIPLE: 'multiple',
  INPUT: 'input',
  DATE: 'date',
  DATE_RANGE: 'dateRange',
  TEXTAREA: 'textarea',
  GROUP: 'group',
} as const;

/**
 * Hook personalizado para manejar filtros con estado interno
 * @param filters - Array de filtros a manejar
 * @param onClear - Callback opcional para limpiar filtros
 * @param onApply - Callback opcional para aplicar filtros
 * @returns Objeto con estado y funciones para manejar filtros
 */
export const useFilters = ({ filters, onClear, onApply }: UseFiltersProps): UseFiltersReturn => {
  // Función helper para obtener valor por defecto según tipo de filtro
  const getDefaultValueByType = useCallback((filterType?: string): FilterValue => {
    switch (filterType) {
      case FILTER_TYPES.MULTIPLE:
      case FILTER_TYPES.GROUP:
        return [];
      case FILTER_TYPES.DATE:
        return null;
      case FILTER_TYPES.DATE_RANGE:
        return { start: null, end: null };
      case FILTER_TYPES.DROPDOWN:
      case FILTER_TYPES.INPUT:
      case FILTER_TYPES.TEXTAREA:
      default:
        return '';
    }
  }, []);

  // Función helper para obtener valor inicial según tipo de filtro
  const getInitialValue = useCallback(
    (filter: Filter): FilterValue => {
      return getDefaultValueByType(filter.type);
    },
    [getDefaultValueByType],
  );

  // Función helper para obtener valor de limpieza según tipo de filtro
  const getClearValue = useCallback(
    (filter: Filter): FilterValue => {
      return getDefaultValueByType(filter.type);
    },
    [getDefaultValueByType],
  );

  // Estado interno para los filtros
  const [filterStates, setFilterStates] = useState<Record<string, FilterValue>>(() => {
    const initialStates: Record<string, FilterValue> = {};
    filters.forEach((filter) => {
      const key = filter.keyName || filter.label.toLowerCase();
      initialStates[key] = getInitialValue(filter);
    });
    return initialStates;
  });

  // Memoizar la función de sincronización para evitar recreaciones innecesarias
  const syncFilterStates = useCallback(() => {
    const newFilterStates: Record<string, FilterValue> = {};

    filters.forEach((filter) => {
      const key = filter.keyName || filter.label.toLowerCase();
      const value = getInitialValue(filter);
      newFilterStates[key] = value;
    });

    setFilterStates(newFilterStates);
  }, [filters, getInitialValue]);

  // Sincronizar estados cuando cambien los filtros
  useEffect(() => {
    syncFilterStates();
  }, [syncFilterStates]);

  // Función para limpiar todos los filtros
  const handleClear = useCallback(() => {
    const clearedStates: Record<string, FilterValue> = {};

    // Limpiar estado interno
    filters.forEach((filter) => {
      const key = filter.keyName || filter.label.toLowerCase();
      clearedStates[key] = getClearValue(filter);
    });

    setFilterStates(clearedStates);

    // Notificar a cada filtro individual
    filters.forEach((filter) => {
      if (filter.onValueChange) {
        const clearValue = getClearValue(filter);
        filter.onValueChange({
          current: clearValue,
          filters: clearedStates,
        });
      }
    });

    // Llamar al callback general de limpieza
    onClear?.();
  }, [filters, getClearValue, onClear]);

  // Función para manejar cambios en filtros individuales
  const handleFilterChange = useCallback(
    (filterLabel: string, newValue: FilterValue, filter: Filter) => {
      const key = filter.keyName || filterLabel.toLowerCase();

      // Actualizar estado interno
      const updatedStates = {
        ...filterStates,
        [key]: newValue,
      };

      setFilterStates(updatedStates);

      // Llamar al callback del filtro con el estado actualizado
      if (filter.onValueChange) {
        filter.onValueChange({
          current: newValue,
          filters: updatedStates,
        });
      }
    },
    [filterStates],
  );

  // Función para aplicar filtros
  const handleApply = useCallback(() => {
    onApply?.(filterStates);
  }, [onApply, filterStates]);

  // Memoizar el objeto de retorno para evitar recreaciones innecesarias
  const returnValue = useMemo<UseFiltersReturn>(
    () => ({
      filterStates,
      handleFilterChange,
      handleClear,
      handleApply,
    }),
    [filterStates, handleFilterChange, handleClear, handleApply],
  );

  return returnValue;
};
